import { Input } from '../Common/Input';
import type { AdditionalInputField } from '@/types/checkout';
import { Email } from '../Common/Email';
import { AdyenInputType } from '@/types/adyen';
import { PayButton } from '../PaymentInputs/PayButton';
import { UnmanagedAddress } from '../Address/UnmanagedAddress';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import type { Address } from '@/api/address';

interface AdyenPayFormUIProps {
  additionalInputFields: AdditionalInputField[];
  fieldValues: Record<string, string | Address>;
  fieldErrors: Record<string, string>;
  fieldTouched: Record<string, boolean>;
  onFieldChange: (field: AdditionalInputField, value: string) => void;
  onFieldBlur: (field: AdditionalInputField, value: string) => void;
  onAddressSelect: (field: AdditionalInputField, address: Address) => void;
  onPayment: () => void;
  isDisabled?: boolean;
  handleTaxValidationChange: (isValid: boolean) => void;
}

export const AdyenPayFormUI = ({
  additionalInputFields,
  fieldValues,
  fieldErrors,
  fieldTouched,
  onFieldChange,
  onFieldBlur,
  onAddressSelect,
  onPayment,
  isDisabled = false,
  handleTaxValidationChange,
}: AdyenPayFormUIProps) => {
  const renderFieldInput = (field: AdditionalInputField) => {
    const rawValue = fieldValues[field.apm_key];
    const stringValue = typeof rawValue === 'string' ? rawValue : '';
    switch (field.apm_key) {
      case AdyenInputType.SHOPPER_EMAIL:
      case AdyenInputType.EMAIL:
        return <Email />;

      case AdyenInputType.SHOPPER_NAME:
        return (
          <Input
            {...field}
            className="w-full"
            placeholder={field.display_name}
            value={stringValue}
            onChange={(e) => onFieldChange(field, e.target.value)}
            onBlur={() => onFieldBlur(field, stringValue)}
          />
        );

      case AdyenInputType.BILLING_ADDRESS:
        return <UnmanagedAddress onSelect={(address) => onAddressSelect(field, address)} />;

      case AdyenInputType.TELEPHONE_NUMBER:
        return (
          <Input
            {...field}
            className="w-full"
            placeholder={field.display_name}
            value={stringValue}
            onChange={(e) => onFieldChange(field, e.target.value)}
            onBlur={() => onFieldBlur(field, stringValue)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {additionalInputFields.map((field: AdditionalInputField) => {
        const input = renderFieldInput(field);
        if (input === null) return null;
        const error = fieldErrors[field.apm_key];
        const touched = fieldTouched[field.apm_key];

        return (
          <div key={field.apm_key} className="flex flex-col gap-1 w-full">
            {input}
            {error && touched && <div className="text-xs p-1 text-red-500">{error}</div>}
          </div>
        );
      })}
      <InputTaxComponent onValidate={handleTaxValidationChange} />
      <PayButton onClick={onPayment} isDisabled={isDisabled} />
    </div>
  );
};
